/** `audio` — an audio tour, one chapter at a time. */

import { useState } from 'react';
import type { AudioPlayerProps } from '../generated/schemas';
import { stripEmoji as plain } from '../../lib/emoji';

function length(seconds: number | undefined): string | null {
  if (typeof seconds !== 'number' || seconds <= 0) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function AudioPlayer({ props }: { props: AudioPlayerProps }) {
  // `current` is the chapter the graph left off on; the visitor's own paging
  // takes over from there.
  const [index, setIndex] = useState(() => {
    const wanted = props.chapters.findIndex((chapter) => chapter.n === props.current);
    return wanted >= 0 ? wanted : 0;
  });
  const chapter = props.chapters[Math.min(index, props.chapters.length - 1)];
  if (!chapter) return null;
  const duration = length(chapter.length_s);

  return (
    <article className="card card--audio">
      <header className="card__head">
        <h3 className="card__title">{plain(props.title ?? 'Аудиогид')}</h3>
        <span className="card__meta">
          Глава {index + 1} из {props.chapters.length}
        </span>
      </header>
      <div className="audio">
        <div className="audio__title">
          {plain(chapter.title)}
          {duration ? <span className="audio__len"> · {duration}</span> : null}
        </div>
        {chapter.subtitle ? <div className="audio__sub">{plain(chapter.subtitle)}</div> : null}
        {/* `src` is a museum media host, which is why `media-src` is open on
            https: while `script-src` is not (§7.2). */}
        <audio className="audio__player" controls preload="none" src={chapter.src}>
          Ваш браузер не умеет проигрывать аудио.
        </audio>
      </div>
      {props.chapters.length > 1 ? (
        <footer className="card__actions">
          <button
            type="button"
            className="chip"
            disabled={index === 0}
            onClick={() => setIndex((current) => Math.max(0, current - 1))}
          >
            ← Предыдущая
          </button>
          <button
            type="button"
            className="chip"
            disabled={index >= props.chapters.length - 1}
            onClick={() => setIndex((current) => Math.min(props.chapters.length - 1, current + 1))}
          >
            Следующая глава →
          </button>
        </footer>
      ) : null}
    </article>
  );
}
